import { ROUTES } from '@/constants/routes';
import { queryClient } from '@/main';
import { fetchRemoteConfig, getConfig } from '@/utils/remoteConfig';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { clearTokens, getTokens, hasTokens, setTokens } from '../utils/cookies';

export interface AuthResponse {
  access: string;
  refresh?: string;
}

await fetchRemoteConfig();

const API_BASE_URL = getConfig('api_base_url') || '';

/**
 * Main API client
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * Separate Axios instance for refresh to prevent recursive interceptor loops
 */
const refreshClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/`,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshingPromise: Promise<string> | null = null;

/**
 * Refresh token logic
 */
const refreshAccessToken = async (): Promise<string> => {
  if (!hasTokens()) {
    clearTokens();
    throw new Error('Missing refresh token');
  }

  const tokens = getTokens();

  try {
    const response = await refreshClient.post<AuthResponse>(
      'users/token/refresh/',
      { refresh: tokens!.refreshToken }
    );

    setTokens({ access: response.data.access });
    return response.data.access;
  } catch (err) {
    clearTokens();
    throw err;
  }
};

/**
 * Ensures ONLY ONE concurrent refresh is executed
 */
const getFreshAccessToken = async (): Promise<string> => {
  if (!refreshingPromise) {
    refreshingPromise = refreshAccessToken().finally(() => {
      refreshingPromise = null;
    });
  }
  return refreshingPromise;
};

/**
 * Request interceptor — attach token
 */
apiClient.interceptors.request.use((config) => {
  const tokens = getTokens();

  if (tokens?.accessToken) {
    config.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }

  return config;
});

/**
 * Response interceptor — handle auto refresh & retry
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,

  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const status = error.response?.status;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await getFreshAccessToken();

        originalRequest.headers.set(
          'Authorization',
          `Bearer ${newAccessToken}`
        );

        return apiClient(originalRequest);
      } catch (refreshError) {
        queryClient.clear();
        clearTokens();
        window.location.href = ROUTES.LOGIN;
        return Promise.reject(refreshError);
      }
    }

    // if (status === 500) {
    //   window.location.href = `${ROUTES.SERVER_ERROR}`;
    //   return Promise.reject(error);
    // }

    if (status === 403) {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
      window.location.href = ROUTES.FORBIDDEN;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
